import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { FCC } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import {
  useSession,
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { InterstitialsContext } from './InterstitialsContext';
import { UserRegistrationInterstitial } from './interstitials/UserRegistrationInterstitial';
import { VideoWalkthroughInterstitial } from './interstitials/VideoWalkthroughInterstitial';
import { getUserFlowCompletionsQuery } from './operations';
import {
  BaseInterstitial,
  InterstitialContext,
  InterstitialsContextType,
} from './types';

// Static list of available interstitials, sorted by priority (highest first)
export const availableInterstitials = [
  UserRegistrationInterstitial,
  VideoWalkthroughInterstitial,
].sort((a, b) => b.priority - a.priority);

export const InterstitialsProvider: FCC = ({ children }) => {
  const logger = useNamedLogger('InterstitialsProvider');

  const tenantId = useTenantIdFromParams() as string;
  const session = useSession();
  const userId = useUserIdFromSession();
  const location = useLocation();

  const { data, loading: completionsLoading } = useQuery(
    getUserFlowCompletionsQuery,
    {
      skip: !userId || !tenantId,
    },
  );

  const interstitialContext = useMemo<InterstitialContext>(
    () => ({
      userId,
      tenantId,
      data: data || { userInterstitialFlows: [] },
      currentPath: location.pathname,
      userSession: session,
      logger,
    }),
    [userId, tenantId, data, location.pathname, session, logger],
  );

  const shouldShowInterstitial = useCallback(
    (interstitial: BaseInterstitial, context: InterstitialContext): boolean => {
      // Check path matching first (fast check)
      if (interstitial.showOnPaths && interstitial.showOnPaths.length > 0) {
        const matchesPath = interstitial.showOnPaths.some((pathRegex) =>
          context.currentPath?.match(pathRegex),
        );
        if (!matchesPath) {
          return false;
        }
      }

      // Run the custom logic
      return interstitial.shouldShow(context);
    },
    [],
  );

  const activeInterstitial = useMemo(() => {
    if (completionsLoading || !userId) {
      return null;
    }

    const active =
      availableInterstitials.find((interstitial) =>
        shouldShowInterstitial(interstitial, interstitialContext),
      ) || null;

    if (active) {
      logger.info('Showing interstitial', { name: active.name });
    }

    return active;
  }, [
    interstitialContext,
    completionsLoading,
    userId,
    shouldShowInterstitial,
    logger,
  ]);

  const contextValue = useMemo<InterstitialsContextType>(
    () => ({
      activeInterstitial,
      isLoading: completionsLoading,
      context: completionsLoading || !userId ? null : interstitialContext,
    }),
    [activeInterstitial, completionsLoading, userId, interstitialContext],
  );

  return (
    <InterstitialsContext.Provider value={contextValue}>
      {children}
    </InterstitialsContext.Provider>
  );
};
